export const SIGNUP_PLAN_STORAGE_KEY = 'smartdata.signup.plan';

const PLAN_QUERY_KEYS = ['plan', 'billing_plan', 'plan_slug', 'byo_plan'];
const BYO_PLAN_LABELS = Object.freeze({
    'byo-pass': 'BYO Dataset Pass',
    'byo-start': 'BYO Starter',
    'byo-pro': 'BYO Pro',
    'byo-ent': 'BYO Enterprise'
});
const PLAN_ALIASES = Object.freeze({
    'byo-dataset': 'byo-pass',
    'byo-dataset-pass': 'byo-pass',
    'byo-one': 'byo-pass',
    'byo-single': 'byo-pass',
    'byo-promo': 'byo-pass',
    'byo-starter': 'byo-start',
    'byo-team': 'byo-pro',
    'byo-custom': 'byo-ent',
    'byo-enterprise': 'byo-ent',
    'byo-enterprise-custom': 'byo-ent'
});

export function normaliseSignupPlanSlug(planSlug) {
    const slug = String(planSlug || '')
        .trim()
        .toLowerCase();
    return PLAN_ALIASES[slug] || slug;
}

export function isByoSignupPlanSlug(planSlug) {
    return normaliseSignupPlanSlug(planSlug).startsWith('byo-');
}

export function formatSignupPlanLabel(planSlug) {
    const normalisedPlanSlug = normaliseSignupPlanSlug(planSlug);
    return BYO_PLAN_LABELS[normalisedPlanSlug] || normalisedPlanSlug;
}

export function getSignupPlanFromSearch(search = '') {
    const params = new URLSearchParams(search || '');

    for (const key of PLAN_QUERY_KEYS) {
        const value = normaliseSignupPlanSlug(params.get(key));
        if (value) return value;
    }

    return '';
}

export function getStoredSignupPlan(storage = window.localStorage) {
    try {
        return normaliseSignupPlanSlug(storage.getItem(SIGNUP_PLAN_STORAGE_KEY));
    } catch {
        return '';
    }
}

export function storeSignupPlanFromSearch(
    search = window.location.search,
    storage = window.localStorage
) {
    const requestedPlanSlug = getSignupPlanFromSearch(search);
    if (!requestedPlanSlug) return '';

    try {
        storage.setItem(SIGNUP_PLAN_STORAGE_KEY, requestedPlanSlug);
    } catch {
        return requestedPlanSlug;
    }

    return requestedPlanSlug;
}

export function getRequestedSignupPlanSlug(
    search = window.location.search,
    storage = window.localStorage
) {
    return getSignupPlanFromSearch(search) || getStoredSignupPlan(storage);
}

export function clearStoredSignupPlan(storage = window.localStorage) {
    try {
        storage.removeItem(SIGNUP_PLAN_STORAGE_KEY);
    } catch {
        // Ignore storage failures; plan selection should still continue.
    }
}

export function buildPlansUrl(requestedPlanSlug) {
    const planSlug = normaliseSignupPlanSlug(requestedPlanSlug);
    if (!planSlug) return '/accounts/plans';

    return `/accounts/plans?plan=${encodeURIComponent(planSlug)}`;
}

export function buildPlanSelectionUrl(requestedPlanSlug) {
    const planSlug = normaliseSignupPlanSlug(requestedPlanSlug);
    if (!planSlug) return '/accounts/plan/select/';

    return `/accounts/plan/select/?plan=${encodeURIComponent(planSlug)}`;
}

export function buildUploadUrl(options = {}) {
    const teamId = String(options.teamId || options.team || '').trim();
    const billingAccountId = String(
        options.billingAccountId || options.billingAccount || ''
    ).trim();
    const startNewDataset = Boolean(
        options.newDataset || options.newAsset || options.createAsset
    );
    const params = new URLSearchParams();

    if (teamId) params.set('team', teamId);
    if (billingAccountId) params.set('billing_account', billingAccountId);
    if (startNewDataset) params.set('new_asset', '1');

    const queryString = params.toString();
    return queryString ? `/upload?${queryString}` : '/upload';
}

export function buildBillingAccountManageUrl(billingAccountId = '') {
    const accountId = String(billingAccountId || '').trim();
    if (!accountId) return '/billing';

    return `/billing/${encodeURIComponent(accountId)}/manage/`;
}

export function planIsByoHosting(plan = {}) {
    return Boolean(plan?.byo_hosting?.is_byo_hosting_plan);
}

export function getPlanBillingInterval(plan = {}) {
    const frequency = Number(
        plan?.frequency ?? plan?.byo_hosting?.pricing?.frequency_months
    );

    if (frequency === 12) return 'annual';
    if (frequency === 3) return 'quarterly';
    return 'monthly';
}

export function buildPaymentIntroCopy(plan = {}) {
    const isByoPlan = planIsByoHosting(plan);
    const planName =
        plan?.name ||
        (isByoPlan ? 'the selected BYO hosting plan' : 'your paid plan');

    if (isByoPlan) {
        const billingInterval = getPlanBillingInterval(plan);
        return (
            `Please add your credit card to activate ${planName}. ` +
            'BYO uploads and hosted viewer access are enabled once Stripe ' +
            `confirms the ${billingInterval} hosting plan.`
        );
    }

    return (
        `Please add your credit card below to activate ${planName}. ` +
        'Payment is collected immediately once Stripe confirms your subscription.'
    );
}

export function buildPaymentSubmitLabel(plan = {}) {
    if (planIsByoHosting(plan)) {
        return normaliseSignupPlanSlug(plan?.slug) === 'byo-pass'
            ? 'Pay for dataset pass'
            : 'Pay now';
    }

    return 'Start Subscription';
}

export function flattenPlansByCurrency(plansByCurrency = {}) {
    return Object.values(plansByCurrency).flatMap(plans =>
        Array.isArray(plans) ? plans : []
    );
}

export function findPlanByRequestedSlug(plansByCurrency, requestedPlanSlug) {
    const planSlug = normaliseSignupPlanSlug(requestedPlanSlug);
    if (!planSlug) return null;

    return (
        flattenPlansByCurrency(plansByCurrency).find(plan => {
            const slugMatches =
                normaliseSignupPlanSlug(plan?.slug) === planSlug;
            const idMatches = String(plan?.id || '').toLowerCase() === planSlug;
            return slugMatches || idMatches;
        }) || null
    );
}
